import { useCustomerProvider } from '@material-bank/customer';
import { shouldShowSimilarItems } from '@material-bank/products-utils';
import { Project as ProjectModel } from '@material-bank/projects-api';
import {
  useCurrentProject,
  useProjectsProvider,
} from '@material-bank/projects-utils';
import { Project, pushGtmDataLayer } from '@mb/lib';
import { handleSystemErrorReport } from '@mb/lib/utils';
import { useMediaQuery } from 'react-responsive';
import { useAPICallStateHandler } from './customHooks';
import {
  getIsTierAllowed as getIsRecommendationModalTierAllowed,
  getRecommendationsModalSettings,
} from '../../../../../../Recommendation/view/frontend/app/api/localStorageCalls';
import {
  OpenRecomendationsModalPayload,
  OpenServicesModalPayload,
} from '../../../../../../Recommendation/view/frontend/app/types/recommendationsModal.types';
import { sendAnalytics } from '../api/events';
import { client } from '../api/http';
import { endpoints } from '../config';
import { getUserAttributes } from '../helpers';
import { getIsPaintProductType } from '../helpers';
import { pushSearchAnalyticsDataLayer } from '../helpers/analytics';
import {
  addCartItemsInQueue,
  setSnackbarInfo,
  SnackbarInfo,
  setMaterialsInQueue,
} from '../redux/sliceCreators/commonSlice';
import { setIsProjectModalOpen } from '../redux/sliceCreators/projectsSlice';
import { useAppDispatch } from '../redux/store';

import { ResponseStatusEnum, SnackbarStatusEnum } from '../types';
import { ProductSearchMaterial } from '../types/material';

const isRMDataInLocalStorage = Boolean(getRecommendationsModalSettings());

declare let MEDIA_URL: string;

interface RelatedProduct {
  title: string;
  id: string | number;
}

// TODO: refactor the entire hook
export const useAddToBag = () => {
  const dispatch = useAppDispatch();
  const { activeProjects } = useProjectsProvider();
  const { state, setState } = useAPICallStateHandler();
  const { currentProject } = useCurrentProject();
  const { customer } = useCustomerProvider();
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
  const isRMtierAllowed = getIsRecommendationModalTierAllowed();

  const addToBag = async (
    materials: ProductSearchMaterial[],
    projectId: string | number | null,
    project?: Project,
    relatedProductData?: RelatedProduct,
    triggerLocation = 'Catalog',
    _: boolean = false,
    shouldTriggerModal: boolean = true,
    customSuccessMessage?: Pick<SnackbarInfo, 'messagePayload' | 'messageType'>,
    onSuccess?: () => void,
  ) => {
    try {
      if (Object.keys(activeProjects)?.length === 0 && project == null) {
        if (Array.isArray(materials)) {
          dispatch(setMaterialsInQueue(materials));
        } else {
          dispatch(setMaterialsInQueue([materials]));
        }
        dispatch(setIsProjectModalOpen(true));
        return;
      }

      setState(() => ({
        isLoading: true,
      }));

      const params = new URLSearchParams();
      const bagProject = getBagProject({
        project,
        projectId,
        activeProjects,
        currentProject,
      });

      if (bagProject == null) {
        throw new Error('Please select a project');
      }

      const splitObj: {
        sku: string;
        'ATC-location': string;
        'ATC-collections'?: string;
      } = {
        sku: materials?.map((item) => item.sku).join(','),
        'ATC-location': 'SRP',
      };

      if (window.location.pathname.includes('collections/')) {
        splitObj['ATC-collections'] =
          materials.length > 1 ? 'ATC-multiselect' : 'ATC-individual';
        splitObj['ATC-location'] = 'collections';
      }

      pushGtmDataLayer({
        event: 'add-to-cart',
        value: {
          entityIds: materials?.map((item) => item.entityId),
        },
      });

      const currentMaterial =
        materials.find((item) => !item.isInRealTimeStock) || materials?.[0];
      const isPaint =
        currentMaterial?.isPaint || getIsPaintProductType(currentMaterial);
      const materialSku = materials
        .map((material) => String(material.colorwayId || material.entityId))
        ?.splice(0, 1);
      const isGpsCompliant = !!currentMaterial?.isGpsCompliant;

      // SIM modal logic:
      // - For Gensler users: only show if product is NOT GPS compliant
      // - For all other users: always show (existing logic)
      const shouldAllowRecommendationsModal = shouldShowSimilarItems({
        isGpsCompliant,
        isGenslerCustomer: !!customer?.isGenslerCustomer,
      });

      const shouldShowRecommendationsModal =
        (!isPaint && shouldTriggerModal) || !currentMaterial?.isInRealTimeStock;

      if (shouldShowRecommendationsModal) {
        window.dispatchEvent(
          new CustomEvent<OpenRecomendationsModalPayload>(
            'open-recommendations-modal',
            {
              detail: {
                inProgress: true,
                shouldShowProposal: true,
                skus: materialSku,
                isInRealTimeStock: currentMaterial?.isInRealTimeStock ?? false,
                materialId: String(currentMaterial?.entityId),
                bagProject: {
                  project_id:
                    // @ts-expect-error suppressing error until a refactor is done
                    bagProject?.externalId || bagProject?.project_id,
                  project_title:
                    // @ts-expect-error suppressing error until a refactor is done
                    bagProject?.title || bagProject?.project_title,
                  project_type:
                    // @ts-expect-error suppressing error until a refactor is done
                    bagProject?.type?.externalId ||
                    // @ts-expect-error suppressing error until a refactor is done
                    bagProject?.project_type,
                },
                addedMaterialImage: currentMaterial?.thumbnailImageUrl ?? null,
                manufacturer: currentMaterial?.manufacturer,
                shouldHideRecommendations: !shouldAllowRecommendationsModal,
              },
            },
          ),
        );
      }

      materials.forEach((item, index) => {
        params.append(`product_id[${index}]`, item.entityId);
        params.append(`qty[${index}]`, '1');
        params.append(
          `project_name[${index}]`,
          // @ts-expect-error suppressing error until a refactor is done
          bagProject?.title || bagProject?.project_title,
        );
        params.append(
          `project_type[${index}]`,
          // @ts-expect-error suppressing error until a refactor is done
          bagProject?.type?.externalId?.toString() || bagProject?.project_type,
        );
        params.append(
          `project_id[${index}]`,
          // @ts-expect-error suppressing error until a refactor is done
          bagProject?.externalId || bagProject?.project_id,
        );
        params.append(`item_list_id[${index}]`, 'visual_search_samples');
        params.append(`item_list_name[${index}]`, 'Visual Search');
        params.append(`index[${index}]`, String(item?.gridIndex || index + 1));

        if (relatedProductData?.title != null) {
          params.append(`related_product[${index}]`, relatedProductData.title);
        }
        if (relatedProductData?.id != null) {
          params.append(
            `related_product_id[${index}]`,
            String(relatedProductData.id),
          );
        }
      });
      const { data } = await client.post(endpoints.addToBag, params);
      const services = await fetchServices(currentMaterial.entityId);
      if (data?.status === ResponseStatusEnum.FAILED) {
        dispatch(
          setSnackbarInfo({
            isOpen: true,
            message: data?.message ?? 'Some error occurred',
            status: SnackbarStatusEnum.DANGER,
          }),
        );

        window.dispatchEvent(new CustomEvent('close-recommendations-modal'));
      } else {
        const isServerMessage = data?.message?.length > 0;
        const cartItemsLength = data?.total_qty ?? 0;
        const addedMaterialsLength =
          materials.length >= cartItemsLength
            ? cartItemsLength
            : cartItemsLength - materials.length;
        const snackbarMessage = isServerMessage
          ? 'Items added to cart'
          : `${materials.length} items added to cart`;
        const isInStock = currentMaterial?.isInRealTimeStock;

        // If at least 1 material was added to cart
        if (addedMaterialsLength > 0 && !isServerMessage) {
          if (!isPaint && shouldTriggerModal) {
            if (isInStock) {
              if (services != null) {
                window.dispatchEvent(
                  new CustomEvent<OpenServicesModalPayload>(
                    'open-services-modal',
                    {
                      detail: {
                        inProgress: false,
                        shouldShowProposal: true,
                        serverMessage: null,
                        skus: materialSku,
                        isInRealTimeStock: true,
                        materialId: String(currentMaterial?.entityId),
                        bagProject: {
                          project_id:
                            // @ts-expect-error suppressing error until a refactor is done
                            bagProject?.externalId || bagProject?.project_id,
                          project_title:
                            // @ts-expect-error suppressing error until a refactor is done
                            bagProject?.title || bagProject?.project_title,
                          project_type:
                            // @ts-expect-error suppressing error until a refactor is done
                            bagProject?.type?.externalId ||
                            // @ts-expect-error suppressing error until a refactor is done
                            bagProject?.project_type,
                        },
                        addedMaterialImage:
                          currentMaterial?.thumbnailImageUrl ?? null,
                        currentMaterial,
                        service: services,
                      },
                    },
                  ),
                );
              }
            } else {
              window.dispatchEvent(
                new CustomEvent<OpenRecomendationsModalPayload>(
                  'open-recommendations-modal',
                  {
                    detail: {
                      inProgress: false,
                      shouldShowProposal: true,
                      serverMessage: null,
                      skus: materialSku,
                      isInRealTimeStock:
                        currentMaterial?.isInRealTimeStock ?? false,
                      materialId: String(currentMaterial?.entityId),
                      bagProject: {
                        project_id:
                          // @ts-expect-error suppressing error until a refactor is done
                          bagProject?.externalId || bagProject?.project_id,
                        project_title:
                          // @ts-expect-error suppressing error until a refactor is done
                          bagProject?.title || bagProject?.project_title,
                        project_type:
                          // @ts-expect-error suppressing error until a refactor is done
                          bagProject?.type?.externalId ||
                          // @ts-expect-error suppressing error until a refactor is done
                          bagProject?.project_type,
                      },
                      addedMaterialImage:
                        currentMaterial?.thumbnailImageUrl ?? null,
                      currentMaterial,
                      manufacturer: currentMaterial?.manufacturer,
                      shouldHideRecommendations:
                        !shouldAllowRecommendationsModal,
                    },
                  },
                ),
              );
            }
          }
        } else if (isServerMessage) {
          const productThumbnail =
            data?.productThumbnail != null
              ? `${MEDIA_URL}catalog/product${data?.productThumbnail}`
              : null;
          const errorMaterialImage =
            productThumbnail ||
            formatControlRulesImageUrl(data?.modal_data?.[0]?.image_url) ||
            currentMaterial?.thumbnailImageUrl;
          const controlRuleId = data?.modal_data?.[0]?.control_rule_id ?? null;

          const guardrailsDailyLimitReached =
            data.modal_data?.[0]?.guardrails_daily_limit_reached ?? false;

          window.dispatchEvent(
            new CustomEvent<OpenRecomendationsModalPayload>(
              'open-recommendations-modal',
              {
                detail: {
                  forceAllow: true,
                  inProgress: false,
                  shouldShowProposal: false,
                  serverMessage: data?.message,
                  skus: materialSku,
                  isInRealTimeStock: false,
                  materialId: String(currentMaterial?.entityId),
                  bagProject: {
                    project_id:
                      // @ts-expect-error suppressing error until a refactor is done
                      bagProject?.externalId || bagProject?.project_id,
                    project_title:
                      // @ts-expect-error suppressing error until a refactor is done
                      bagProject?.title || bagProject?.project_title,
                    project_type:
                      // @ts-expect-error suppressing error until a refactor is done
                      bagProject?.type?.externalId ||
                      // @ts-expect-error suppressing error until a refactor is done
                      bagProject?.project_type,
                  },
                  addedMaterialImage: errorMaterialImage ?? null,
                  controlRuleId,
                  manufacturer: currentMaterial?.manufacturer,
                  guardrailsDailyLimitReached,
                  shouldHideRecommendations: !shouldAllowRecommendationsModal,
                },
              },
            ),
          );
        }

        // If more than 1 material was added to cart
        if (
          addedMaterialsLength > 0 &&
          !(isServerMessage && materials.length === 1)
        ) {
          const isOutOfStock = !currentMaterial?.isInRealTimeStock;
          const itemHasServices =
            currentMaterial?.isInRealTimeStock && services != null;
          const rmWillShow =
            !isMobile &&
            isRMtierAllowed &&
            isRMDataInLocalStorage &&
            !isPaint &&
            shouldTriggerModal &&
            (isOutOfStock || itemHasServices);

          if (!rmWillShow || materials.length > 1) {
            if (!isServerMessage) {
              if (onSuccess && typeof onSuccess === 'function') {
                onSuccess();

                if (customSuccessMessage) {
                  dispatch(
                    setSnackbarInfo({
                      isOpen: true,
                      message: undefined,
                      status: SnackbarStatusEnum.DEFAULT,
                      ...customSuccessMessage,
                    }),
                  );
                }
              } else {
                dispatch(
                  setSnackbarInfo({
                    isOpen: true,
                    status: SnackbarStatusEnum.DEFAULT,
                    message:
                      materials.length > 1 ? snackbarMessage : 'Added to cart',
                  }),
                );
              }
            }
            // window.dispatchEvent(new CustomEvent("show-brand-snackbar"));
          }

          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent('reload-customer-section-data'),
            );
          }, 500);

          const idsList = materials.map((item) => String(item.entityId));
          const cartItems = materials.map((item) => {
            return {
              entityId: item.entityId,
              time: Date.now(),
            };
          });
          dispatch(addCartItemsInQueue(cartItems));
          sendAnalytics({ id: idsList, type: 'cart' });

          // Analytics
          pushSearchAnalyticsDataLayer(
            materials,
            '',
            'visual_search',
            'Visual Search',
            'add_to_cart',
            triggerLocation,
          );
        }
      }
      dispatch(setMaterialsInQueue([]));

      setState(() => ({
        isLoading: false,
      }));
      window.dispatchEvent(new CustomEvent('trigger-ga-data'));
    } catch (error) {
      if (error.message) {
        window.dispatchEvent(new CustomEvent('close-recommendations-modal'));

        dispatch(
          setSnackbarInfo({
            isOpen: true,
            status: SnackbarStatusEnum.DANGER,
            message: error.message ?? 'Some error occurred',
          }),
        );
        handleSystemErrorReport(
          `Error addToBag: ${error.message ?? 'Some error occurred'}`,
          'addToBag',
        );
      }
      setState({ isLoading: false, error });
      window.dispatchEvent(new CustomEvent('trigger-ga-data'));
    }
  };

  return { ...state, addToBag };
};

type GetBagProjectParams = {
  project?: Project;
  projectId?: string | number | null;
  activeProjects: Record<string, ProjectModel>;
  currentProject?: ProjectModel | null;
};

const getBagProject = ({
  project,
  projectId,
  activeProjects,
  currentProject,
}: GetBagProjectParams): ProjectModel | Project | null => {
  if (project) {
    return project;
  }

  if (projectId && activeProjects[projectId]) {
    return activeProjects[projectId];
  }

  if (currentProject) {
    return currentProject;
  }

  return Object.values(activeProjects)?.[0] || null;
};

function formatControlRulesImageUrl(strUrl: string | null | undefined) {
  if (strUrl) {
    return strUrl.startsWith('//') ? `https:${strUrl}` : strUrl;
  } else {
    return null;
  }
}

export const fetchServices = async (productId: string) => {
  try {
    const bodyFormData = new FormData();
    bodyFormData.append('product_id[0]', productId);
    const { data } = await client.post(endpoints.getServices, bodyFormData);
    const response = data.response ? data : null;

    return response;
  } catch (error) {
    handleSystemErrorReport(
      `Error fetchServices: ${error.message}`,
      'fetchServices',
    );
    return null;
  }
};

export const getConfigurableProduct = async (productSku: string) => {
  const userData = getUserAttributes();
  try {
    const response = await fetch(
      `${endpoints.getConfigurableProduct}/${productSku}`,
      {
        headers: {
          Authorization: `Bearer ${userData.signin_token}`,
          'Content-Type': 'application/json',
        },
      },
    );
    return response.json();
  } catch (e) {
    handleSystemErrorReport(
      `Error getConfigurableProduct: ${e.message}`,
      'getConfigurableProduct',
    );
    return null;
  }
};
